import { useState, useEffect, useRef } from "react";
import { QuestionnaireQuestion, QuestionnaireAnswers } from "../../types/questionnaire";
import { QuestionRenderer } from "./QuestionRenderer";
import { ProgressBar } from "../ui/ProgressBar";
import { Button } from "../../components/ui/button";
import { Header } from "../Header";
import {
  getVisibleQuestions,
  getCurrentQuestionIndex,
  isQuestionAnswered,
  isQuestionnaireComplete,
  shouldAutoAdvanceOnAnswer,
  shouldHideContinueForAutoAdvance,
  type QuestionnaireAnswerValue,
} from "../../lib/questionnaire-engine";
import {
  getNoneOfTheAboveValues,
  sanitizeMultipleOptionAnswer,
} from "../../lib/none-of-the-above";
import { getQuestionnaireTranslations } from "../../lib/questionnaire-translations";
import {
  getContinueButtonTestId,
  getRejectionBackButtonTestId,
  getRejectionCloseButtonTestId,
} from "../../lib/test-ids";
import type { ProgressSectionId } from "../../lib/progress-calculator";

const focusFirstQuestionControl = () => {
  const group = document.querySelector<HTMLElement>('[aria-invalid="true"]');
  const focusable = group?.querySelector<HTMLElement>(
    'input:not([type="hidden"]), textarea, button:not([disabled]), [tabindex="0"]'
  );
  const target = focusable || group;
  if (!target) return;
  target.focus();
  target.scrollIntoView({ block: "center", behavior: "smooth" });
};

// Question IDs that should have a "Skip" button (message to doctor questions)
const SKIPPABLE_QUESTION_IDS = [
  "8310421f-ae56-4eef-b8f5-563b5a8d841c", // English - message to doctor
  "afcd2c8b-d802-4a0a-8e4d-24c952c4510d",
  "113587f0-3bdf-48a2-abb4-728322c3519c",
  "4331230b-a18a-4164-9a2f-fcb271b3d4fd",
  "6b989660-5e64-441d-97d1-43d100a16c3f", // File upload - lab results (not yet supported)
  "a9cf49cd-c132-4990-bc11-8137721d0a06", // File upload - lab results (not yet supported)
];

interface QuestionnaireProps {
  questions: QuestionnaireQuestion[];
  answers: QuestionnaireAnswers;
  setAnswers: (answers: QuestionnaireAnswers) => void;
  onComplete: (answers: QuestionnaireAnswers) => void;
  language?: "en" | "es";
  className?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  onQuestionChange?: (questionId: string) => void;
  logoUrl?: string;
  currentSavedQuestionId: string;
  onClose?: () => void;
  onExitQuestionnaire: () => void;
  updateCookie: (progress: string, data?: object, overrideCaseId?: string) => void;
  cookie: object;
  savedCaseId?: string;
  hideBackOnFirstQuestion?: boolean;
  /** SRX-202: pause and call `onEmailCaptureInterrupt` instead of advancing once this many
   *  questions have been answered (counting from 1), unless `hasCapturedEmail` is already true. */
  emailCaptureThreshold?: number;
  hasCapturedEmail?: boolean;
  onEmailCaptureInterrupt?: (resumeAtQuestionId: string) => void;
  /** True only when THIS session actually went through the mid-questionnaire interrupt (not for
   *  customers who already had email/were fully authenticated from the start) — gates whether
   *  pressing "back" on the first post-interrupt question steps back through
   *  health-questions-intro/email-capture instead of just moving to the previous question. */
  canGoBackToEmailCapture?: boolean;
  onBackToEmailCapture?: (prevQuestionId: string) => void;
}

export function Questionnaire({
  questions,
  onComplete,
  answers,
  setAnswers,
  language = "en",
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  onQuestionChange,
  logoUrl,
  currentSavedQuestionId,
  onClose,
  onExitQuestionnaire,
  updateCookie,
  savedCaseId = "",
  hideBackOnFirstQuestion = false,
  emailCaptureThreshold,
  hasCapturedEmail = false,
  onEmailCaptureInterrupt,
  canGoBackToEmailCapture = false,
  onBackToEmailCapture,
}: QuestionnaireProps) {
  const [currentQuestionId, setCurrentQuestionId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const caseIdRef = useRef(savedCaseId);

  // Track the last saved question ID we restored to (to detect when it changes)
  const lastRestoredQuestionId = useRef<string | null>(null);

  useEffect(() => {
    caseIdRef.current = savedCaseId;
  }, [savedCaseId]);

  useEffect(() => {
    if (questions.length === 0 || Object.keys(answers).length === 0) return;

    let changed = false;
    const nextAnswers: QuestionnaireAnswers = { ...answers };

    for (const question of questions) {
      if (question.type !== "multiple_option") continue;
      const raw = answers[question.partner_questionnaire_question_id];
      if (!Array.isArray(raw)) continue;

      const sanitized = sanitizeMultipleOptionAnswer(
        raw,
        getNoneOfTheAboveValues(question.options)
      );
      if (
        sanitized.length !== raw.length ||
        sanitized.some((v, i) => v !== raw[i])
      ) {
        nextAnswers[question.partner_questionnaire_question_id] = sanitized;
        changed = true;
      }
    }

    if (changed) {
      setAnswers(nextAnswers);
    }
  }, [questions, answers, setAnswers]);

  const translations = getQuestionnaireTranslations(language);
  const visibleQuestions = getVisibleQuestions(questions, answers);
  const currentIndex = getCurrentQuestionIndex(visibleQuestions, currentQuestionId);
  const currentQuestion = visibleQuestions[currentIndex];
  const isComplete = isQuestionnaireComplete(questions, answers);

  // Restore to saved question ID - waits for questions AND answers to load before attempting
  useEffect(() => {
    // Wait for questions to be loaded (more than just pre-questions)
    // Pre-questions have order < 10, real questions have order >= 10
    const hasRealQuestions = questions.some(q => q.order >= 10);
    if (!hasRealQuestions) {
      return;
    }

    const answersCount = Object.keys(answers).length;

    // If we have a saved question ID to restore to
    if (currentSavedQuestionId) {
      // Skip if we've already restored to this exact question ID
      if (lastRestoredQuestionId.current === currentSavedQuestionId) {
        return;
      }

      // Check if the saved question exists in visible questions
      const questionExists = visibleQuestions.some(
        q => q.partner_questionnaire_question_id === currentSavedQuestionId
      );

      if (questionExists) {
        setCurrentQuestionId(currentSavedQuestionId);
        onQuestionChange?.(currentSavedQuestionId);
        lastRestoredQuestionId.current = currentSavedQuestionId;
        return;
      }

      // Saved question not found in visible list
      // This could be because answers haven't loaded yet (visibleQuestions depends on answers)
      // If we have a saved question but no answers yet, WAIT for answers to arrive
      if (answersCount === 0) {
        return; // Don't fall back to first question - wait for answers
      }

      // We have answers but saved question still not visible - find next unanswered question
      // Find the first unanswered question
      const nextUnansweredQuestion = visibleQuestions.find(q => {
        const questionId = q.partner_questionnaire_question_id;
        return !answers[questionId];
      });

      if (nextUnansweredQuestion) {
        const nextQuestionId = nextUnansweredQuestion.partner_questionnaire_question_id;
        setCurrentQuestionId(nextQuestionId);
        onQuestionChange?.(nextQuestionId);
        lastRestoredQuestionId.current = currentSavedQuestionId; // Mark as restored
        return;
      }

      // All questions answered - go to last question (might be completion screen)
      if (visibleQuestions.length > 0) {
        const lastQuestionId = visibleQuestions[visibleQuestions.length - 1].partner_questionnaire_question_id;
        setCurrentQuestionId(lastQuestionId);
        onQuestionChange?.(lastQuestionId);
        lastRestoredQuestionId.current = currentSavedQuestionId; // Mark as restored
        return;
      }
    }

    // No saved question ID yet - initialize to first question BUT don't block future restoration
    // This allows the saved progress to load asynchronously and still restore
    if (visibleQuestions.length > 0 && !currentQuestionId) {
      const firstQuestionId = visibleQuestions[0].partner_questionnaire_question_id;
      setCurrentQuestionId(firstQuestionId);
      onQuestionChange?.(firstQuestionId);
      // DON'T set lastRestoredQuestionId here - we want to allow restoration when saved progress arrives
    }
  }, [questions.length, visibleQuestions.length, currentSavedQuestionId, answers]); // Re-run when questions load or answers change

  // If the current question is no longer visible (branching), move to the next unanswered
  useEffect(() => {
    if (!currentQuestionId || visibleQuestions.length === 0) return;
    const stillVisible = visibleQuestions.some(
      (q) => q.partner_questionnaire_question_id === currentQuestionId
    );
    if (stillVisible) return;

    const fallback =
      visibleQuestions.find((q) => !answers[q.partner_questionnaire_question_id]) ||
      visibleQuestions[0];
    const fallbackId = fallback.partner_questionnaire_question_id;
    setCurrentQuestionId(fallbackId);
    onQuestionChange?.(fallbackId);
    lastRestoredQuestionId.current = fallbackId;
  }, [visibleQuestions, currentQuestionId, answers, onQuestionChange]);

  const getSanitizedAnswerValue = (
    question: QuestionnaireQuestion,
    answersSnapshot: QuestionnaireAnswers
  ): QuestionnaireAnswerValue | "acknowledged" => {
    if (question.type === "informational") {
      return "acknowledged";
    }

    const raw = answersSnapshot[question.partner_questionnaire_question_id];
    if (question.type === "multiple_option" && Array.isArray(raw)) {
      return sanitizeMultipleOptionAnswer(
        raw,
        getNoneOfTheAboveValues(question.options)
      );
    }
    return raw as QuestionnaireAnswerValue;
  };

  const resolveNextQuestionId = (
    fromQuestionId: string,
    answersSnapshot: QuestionnaireAnswers
  ): string | undefined => {
    const nextVisible = getVisibleQuestions(questions, answersSnapshot);
    const idx = getCurrentQuestionIndex(nextVisible, fromQuestionId);
    if (idx < 0 || idx >= nextVisible.length - 1) return undefined;
    return nextVisible[idx + 1].partner_questionnaire_question_id;
  };

  // SRX-202: fires once, the moment the (emailCaptureThreshold)th question has just been
  // answered/skipped — `passedIndex` is that question's 0-based position, so `passedIndex + 1`
  // is how many questions have now been answered. Saves progress first (mirroring goToQuestion's
  // cookie write) so nothing is lost if the customer closes the tab during the interrupt.
  const interruptForEmailCapture = (
    passedIndex: number,
    nextQuestionId: string,
    answersForSave: QuestionnaireAnswers,
    resolvedCaseId: string
  ): boolean => {
    if (
      hasCapturedEmail ||
      !onEmailCaptureInterrupt ||
      emailCaptureThreshold === undefined ||
      passedIndex + 1 !== emailCaptureThreshold
    ) {
      return false;
    }

    updateCookie(
      "questionnaire",
      { caseId: resolvedCaseId, answers: answersForSave },
      resolvedCaseId
    );
    onEmailCaptureInterrupt(nextQuestionId);
    return true;
  };

  const goToQuestion = (questionId: string, answersForSave: QuestionnaireAnswers, resolvedCaseId: string) => {
    setDirection("forward");
    setCurrentQuestionId(questionId);
    onQuestionChange?.(questionId);
    // Keep restore effect from fighting intentional navigation
    lastRestoredQuestionId.current = questionId;
    updateCookie(
      "questionnaire",
      {
        caseId: resolvedCaseId,
        currentQuestionId: questionId,
        answers: answersForSave,
      },
      resolvedCaseId
    );
  };

  const advanceWithAnswers = (
    answersSnapshot: QuestionnaireAnswers,
    options?: { completeQuestionnaire?: boolean }
  ) => {
    if (!currentQuestion) return;
    setError("");

    const completeQuestionnaire = options?.completeQuestionnaire ?? false;
    const question = currentQuestion;
    const questionId = question.partner_questionnaire_question_id;
    const answerValue = getSanitizedAnswerValue(question, answersSnapshot);

    let answersForSave = answersSnapshot;
    if (question.type === "multiple_option" && Array.isArray(answerValue)) {
      const raw = answersSnapshot[questionId];
      if (
        Array.isArray(raw) &&
        (raw.length !== answerValue.length ||
          raw.some((v, i) => v !== answerValue[i]))
      ) {
        answersForSave = {
          ...answersSnapshot,
          [questionId]: answerValue,
        };
        setAnswers(answersForSave);
      }
    }

    const nextQuestionId = completeQuestionnaire
      ? undefined
      : resolveNextQuestionId(questionId, answersForSave);
    const resolvedCaseId = caseIdRef.current;

    if (
      !completeQuestionnaire &&
      nextQuestionId &&
      interruptForEmailCapture(currentIndex, nextQuestionId, answersForSave, resolvedCaseId)
    ) {
      return;
    }

    if (completeQuestionnaire) {
      updateCookie(
        "questionnaire",
        { caseId: resolvedCaseId, answers: answersForSave },
        resolvedCaseId
      );
      onComplete(answersForSave);
    } else if (nextQuestionId) {
      goToQuestion(nextQuestionId, answersForSave, resolvedCaseId);
    } else {
      updateCookie(
        "questionnaire",
        { caseId: resolvedCaseId, answers: answersForSave },
        resolvedCaseId
      );
    }
  };

  const handleAnswer = (value: QuestionnaireAnswerValue) => {
    if (!currentQuestion) return;

    // Multi-select: auto-advances only when "None of the above" is the sole selection
    if (currentQuestion.type === "multiple_option" || Array.isArray(value)) {
      const nextValue = Array.isArray(value)
        ? sanitizeMultipleOptionAnswer(value, getNoneOfTheAboveValues(currentQuestion.options))
        : value;
      const newAnswers = {
        ...answers,
        [currentQuestion.partner_questionnaire_question_id]: nextValue,
      };
      setAnswers(newAnswers);
      setError("");

      if (!shouldAutoAdvanceOnAnswer(currentQuestion, nextValue)) {
        return;
      }

      const nextId = resolveNextQuestionId(
        currentQuestion.partner_questionnaire_question_id,
        newAnswers
      );
      advanceWithAnswers(newAnswers, {
        completeQuestionnaire: !nextId && isQuestionnaireComplete(questions, newAnswers),
      });
      return;
    }

    const newAnswers = {
      ...answers,
      [currentQuestion.partner_questionnaire_question_id]: value,
    };
    setAnswers(newAnswers);
    setError("");

    if (!shouldAutoAdvanceOnAnswer(currentQuestion, value)) {
      return;
    }

    const nextId = resolveNextQuestionId(
      currentQuestion.partner_questionnaire_question_id,
      newAnswers
    );
    advanceWithAnswers(newAnswers, {
      completeQuestionnaire: !nextId && isQuestionnaireComplete(questions, newAnswers),
    });
  };

  const handleNext = () => {
    if (!currentQuestion) return;

    if (!isQuestionAnswered(currentQuestion, answers)) {
      setError(translations.required);
      requestAnimationFrame(focusFirstQuestionControl);
      return;
    }

    advanceWithAnswers(answers);
  };

  const handleBack = () => {
    setError("");
    setDirection("backward");

    if (currentIndex <= 0) return;

    const prevQuestionId = visibleQuestions[currentIndex - 1].partner_questionnaire_question_id;

    // SRX-202: stepping back off the first post-interrupt question re-enters
    // health-questions-intro/email-capture (in reverse) instead of silently skipping past them.
    if (
      canGoBackToEmailCapture &&
      onBackToEmailCapture &&
      emailCaptureThreshold !== undefined &&
      currentIndex === emailCaptureThreshold
    ) {
      onBackToEmailCapture(prevQuestionId);
      return;
    }

    setCurrentQuestionId(prevQuestionId);
    onQuestionChange?.(prevQuestionId);
  };

  const handleSubmit = () => {
    if (!isQuestionAnswered(currentQuestion, answers)) {
      setError(translations.required);
      requestAnimationFrame(focusFirstQuestionControl);
      return;
    }

    if (!isComplete) return;

    advanceWithAnswers(answers, { completeQuestionnaire: true });
  };

  // Handle skip - records N/A and moves to next question
  const handleSkip = () => {
    if (!currentQuestion) return;

    setError("");

    const newAnswers = {
      ...answers,
      [currentQuestion.partner_questionnaire_question_id]: "N/A",
    };
    setAnswers(newAnswers);

    const nextQuestionId = resolveNextQuestionId(
      currentQuestion.partner_questionnaire_question_id,
      newAnswers
    );
    const resolvedCaseId = caseIdRef.current;

    if (
      nextQuestionId &&
      interruptForEmailCapture(currentIndex, nextQuestionId, newAnswers, resolvedCaseId)
    ) {
      return;
    }

    if (nextQuestionId) {
      goToQuestion(nextQuestionId, newAnswers, resolvedCaseId);
    } else {
      updateCookie(
        "questionnaire",
        { caseId: resolvedCaseId, answers: newAnswers },
        resolvedCaseId
      );
      onComplete(newAnswers);
    }
  };

  // Handler for back button (only shown when not on first question)
  const handleBackOrClose = () => {
    handleBack();
  };

  if (!currentQuestion) {
    return null;
  }

  const isLastQuestion = currentIndex === visibleQuestions.length - 1;
  const showNextButton = !isLastQuestion && currentQuestion.has_next_button;
  const isFirstQuestion = currentIndex === 0;
  const isSkippableQuestion = SKIPPABLE_QUESTION_IDS.includes(
    currentQuestion.partner_questionnaire_question_id
  );
  const hideContinueForAutoAdvance = shouldHideContinueForAutoAdvance(currentQuestion, answers);
  const showContinueButton =
    (showNextButton || isLastQuestion) && !hideContinueForAutoAdvance;

  return (
    <div className="w-full min-h-full flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <Header
        logoUrl={logoUrl}
        onBack={
          isFirstQuestion
            ? (hideBackOnFirstQuestion ? undefined : onExitQuestionnaire)
            : handleBackOrClose
        }
        onClose={onClose}
      />

      <div className="max-w-[640px] mx-auto px-5 pt-8 pb-[30px] lg:px-4 w-full">
        {/* Progress Bar */}
        {currentStep !== undefined &&
          totalSteps !== undefined &&
          sectionIndex !== undefined &&
          totalSections !== undefined &&
          sectionId !== undefined && (
          <ProgressBar
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            language={language}
          />
        )}

        {/* Question Content */}
        <div className="mt-8 mb-[30px]">
          <div
            key={currentQuestion.partner_questionnaire_question_id}
            className={direction === "forward" ? "animate-slide-in-right" : "animate-slide-in-left"}
          >
            <QuestionRenderer
              question={currentQuestion}
              value={answers[currentQuestion.partner_questionnaire_question_id]}
              onChange={handleAnswer}
              error={error}
              language={language}
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <div>
            {currentQuestion.has_next_button === false ? (
              // For cul-de-sac questions (no forward path), show Back and Close buttons
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={handleBack}
                  className="w-full h-[50px] bg-[#2D55FB] hover:bg-[#2347E8] text-white rounded-full flex items-center justify-center transition-colors"
                  data-testid={getRejectionBackButtonTestId(currentQuestion.partner_questionnaire_question_id)}
                >
                  {translations.back || "Back"}
                </Button>
                {onClose && (
                  <Button
                    type="button"
                    onClick={onClose}
                    className="w-full h-[50px] bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full flex items-center justify-center transition-colors"
                    data-testid={getRejectionCloseButtonTestId(currentQuestion.partner_questionnaire_question_id)}
                  >
                    {translations.close || "Close"}
                  </Button>
                )}
              </div>
            ) : showContinueButton ? (
              // For regular questions, show Continue button (and Skip for skippable questions)
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={isLastQuestion ? handleSubmit : handleNext}
                  className="w-full h-[50px] text-white rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] hover:bg-[#2347E8]"
                  data-testid={getContinueButtonTestId(currentQuestion.partner_questionnaire_question_id)}
                >
                  {translations.next}
                </Button>
                {isSkippableQuestion && (
                  <Button
                    type="button"
                    onClick={handleSkip}
                    className="w-full h-[50px] bg-transparent hover:bg-gray-100 text-gray-600 rounded-full flex items-center justify-center transition-colors border border-gray-300"
                  >
                    {translations.skip || "Skip"}
                  </Button>
                )}
              </div>
            ) : null}
        </div>
      </div>
    </div>
  );
}
